import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import '@adornis/forms/x-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as m from '../../../../localization/out/messages.js';
import { MatchingWeekCategory } from '../../../db/category.js';
import { formatFormError } from '../../components/format-form-error.js';
import { entityValidator, translateValidationError } from '../../components/form-validation.js';
import { labeledField } from '../../components/labeled-field.js';
import { subscribeAllMatchingWeekCategories } from '../../../api/category.js';

export const AdminCategories = stateful(() => {
  const categories = useQuery(() => subscribeAllMatchingWeekCategories()({ _id: 1, name: 1, fixedEventDate: 1 }), []);
  const [newCategory, setNewCategory] = useState(new MatchingWeekCategory({}), []);

  const { form, renderField } = useGeneratedForm(
    newCategory,
    {
      translationPrefix: 'admin_categories_form_',
      validator: entityValidator(MatchingWeekCategory),
      translateError: translateValidationError,
    },
    [newCategory],
  );

  const handleCreate = async () => {
    const err = await form.validate();
    if (err) {
      void XSnackbar.show(formatFormError(err, 'admin_categories_form_'));
      return;
    }
    try {
      await newCategory.create();
      setNewCategory(new MatchingWeekCategory({}));
    } catch (e) {
      console.error(e);
      void XSnackbar.show(formatFormError(e, 'admin_categories_form_'));
    }
  };

  if (categories.loading) return html`<div>${m.admin_categories_loading()}</div>`;

  return html`
    <div>
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-bold">${m.admin_categories_title()}</h1>
      </div>

      <div class="card bg-base-100 shadow mb-8">
        <div class="card-body">
          <h2 class="card-title">${m.admin_categories_create_card_title()}</h2>
          <div class="grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end">
            ${labeledField(m.admin_categories_form_name_label(), renderField(['name']))}
            ${labeledField(
              m.admin_categories_form_fixedEventDate_label(),
              renderField(['fixedEventDate'], props => DateInput({ ...props, type: 'date' })),
            )}
            <button class="btn btn-primary" @click=${handleCreate}>
              <span class="icon-[material-symbols--add]"></span>
              ${m.admin_categories_create_button()}
            </button>
          </div>
        </div>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <h2 class="card-title mb-4">${m.admin_categories_existing_card_title()}</h2>
          <div class="overflow-x-auto">
            <table class="table">
              <thead>
                <tr>
                  <th>${m.admin_categories_form_name_label()}</th>
                  <th>${m.admin_categories_form_fixedEventDate_label()}</th>
                  <th class="text-right">${m.admin_categories_table_actions()}</th>
                </tr>
              </thead>
              <tbody>
                ${repeat(
                  categories.value ?? [],
                  c => c._id,
                  c => html`
                    <tr>
                      <td>
                        <input
                          class="input input-bordered w-full"
                          .value=${c.name ?? ''}
                          @input=${(e: Event) => (c.name = (e.currentTarget as HTMLInputElement).value)}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          class="input input-bordered w-full"
                          .value=${c.fixedEventDate ? new Date(c.fixedEventDate).toISOString().split('T')[0] : ''}
                          @change=${(e: Event) => {
                            const value = (e.currentTarget as HTMLInputElement).value;
                            c.fixedEventDate = value ? new Date(value) : undefined;
                          }}
                        />
                      </td>
                      <td class="text-right space-x-2">
                        <button
                          class="btn btn-ghost"
                          @click=${async () => {
                            const confirm = await XNativeDialog.confirm(m.admin_categories_delete_confirm(), {
                              acceptText: m.admin_categories_delete_button(),
                              declineText: m.cancel(),
                            });
                            if (confirm) {
                              await c.remove();
                            }
                          }}
                        >
                          <span class="icon-[material-symbols--delete]"></span>
                          ${m.admin_categories_delete_button()}
                        </button>
                        <button
                          class="btn btn-primary"
                          @click=${async () => {
                            try {
                              await c.validator.validateKeys(c, ['name', 'fixedEventDate'], { aggregateErrors: true });
                              await c.save();
                            } catch (e) {
                              void XSnackbar.show(formatFormError(e, 'admin_categories_form_'));
                            }
                          }}
                        >
                          <span class="icon-[material-symbols--save]"></span>
                          ${m.admin_categories_save_button()}
                        </button>
                      </td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `;
});
